// index.js
import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faSignOutAlt,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import EmployeeModal from '../EmployeeModal';
import { employeeAPI } from '../actions';
import { toast } from 'react-toastify';
import '../../QueueFull/cabinet-zdo-requests.css';
import './style.css';

const EmployeesList = ({ kindergartenId } ) => {
  const [activeTab, setActiveTab] = useState('pedagogical');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeesData, setEmployeesData] = useState({
    pedagogical: [],
    administrative: [],
    technical: [],
    dismissed: []
  });
  const [loading, setLoading] = useState(false);
  const [availablePositions, setAvailablePositions] = useState([]);



  const columns = [
    {
      key: 'fullName',
      label: 'ПІБ',
      sortable: true,
      width: '25%'
    },
    {
      key: 'position',
      label: 'Посада',
      sortable: true,
      width: '25%'
    },
    {
      key: 'hireDate',
      label: 'Дата оформлення на роботу',
      sortable: true,
      width: '20%'
    },
    {
      key: 'units',
      label: 'Кількість шт. од.',
      sortable: true,
      width: '15%'
    },
    {
      key: 'actions',
      label: 'Дії',
      sortable: false,
      width: '15%'
    }
  ];

  // Загрузка данных сотрудников
  const loadEmployees = async () => {
    setLoading(true);
    try {
      let activeEmployees = [];
      let dismissedEmployees = [];

      try {
        activeEmployees = await employeeAPI.getActiveEmployees(kindergartenId);
      } catch (error) {
        console.error('Ошибка загрузки активных сотрудников:', error);
        activeEmployees = [];
      }

      try {
        dismissedEmployees = await employeeAPI.getDismissedEmployees(kindergartenId);
      } catch (error) {
        console.error('Ошибка загрузки уволенных сотрудников:', error);
        dismissedEmployees = [];
      }

      // Группировка сотрудников по категориям на основе positionType
      const groupedData = {
        pedagogical: activeEmployees.filter(emp => 
          emp.departments?.some(dept => dept.positionType === 'Педагогічний')
        ),
        administrative: activeEmployees.filter(emp => 
          emp.departments?.some(dept => dept.positionType === 'Адміністративний')
        ),
        technical: activeEmployees.filter(emp => 
          emp.departments?.some(dept => dept.positionType === 'Технічний')
        ),
        dismissed: dismissedEmployees
      };

      setEmployeesData(groupedData);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      //toast.error('Не вдалося завантажити дані співробітників');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка доступных должностей
  const loadAvailablePositions = async () => {
    try {
      const positions = await employeeAPI.getAvailableStaffPositions(kindergartenId);
      setAvailablePositions(positions);
    } catch (error) {
      console.error('Ошибка загрузки доступных должностей:', error);
      //toast.error('Не вдалося завантажити список посад');
    }
  };

  useEffect(() => {
    loadEmployees();
    loadAvailablePositions();
  }, [kindergartenId]);

  // Функция сортировки
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Сортированные данные
  const sortedData = useMemo(() => {
    const data = employeesData[activeTab] || [];
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Для сортировки по должности берем основную должность
      if (sortConfig.key === 'position') {
        aValue = a.departments?.find(dept => dept.selected)?.positionName || '';
        bValue = b.departments?.find(dept => dept.selected)?.positionName || '';
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [employeesData, activeTab, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, employee) => {
    if (actionId === 'edit') {
      try {
        // Загружаем полные данные сотрудника
        const fullEmployeeData = await employeeAPI.getEmployeeById(kindergartenId, employee.id);
        setSelectedEmployee(fullEmployeeData);
        setIsModalOpen(true);
      } catch (error) {
        console.error('Ошибка загрузки данных сотрудника:', error);
        //toast.error('Не вдалося завантажити дані співробітника');
      }
    } else if (actionId === 'dismiss') {
      // Логика увольнения
      await handleDismissEmployee(employee.id);
    } else if (actionId === 'restore') {
      // Логика восстановления
      await handleRestoreEmployee(employee.id);
    }
    setActiveMenuIndex(null);
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  // Функция для сохранения данных сотрудника
  const handleSaveEmployee = async (employeeData) => {
    try {
      if (employeeData.id) {
        // Редактирование существующего сотрудника
        await employeeAPI.updateEmployee(kindergartenId, employeeData.id, employeeData);
        //toast.success('Дані працівника успішно оновлено!');
      } else {
        // Добавление нового сотрудника
        await employeeAPI.createEmployee(kindergartenId, employeeData);
        //toast.success('Працівника успішно створено!');
      }
      
      // Перезагружаем данные
      await loadEmployees();
      await loadAvailablePositions();
      setIsModalOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Ошибка сохранения сотрудника:', error);
      // toast показывается в API функции
    }
  };

  // Функция для увольнения сотрудника
  const handleDismissEmployee = async (employeeId) => {
    try {
      await employeeAPI.dismissEmployee(kindergartenId, employeeId);
      //toast.success('Працівника успішно звільнено!');
      // Перезагружаем данные
      await loadEmployees();
      await loadAvailablePositions();
    } catch (error) {
      console.error('Ошибка увольнения сотрудника:', error);
      //toast.error('Не вдалося звільнити працівника');
    }
  };

  // Функция для восстановления сотрудника
  const handleRestoreEmployee = async (employeeId) => {
    try {
      await employeeAPI.restoreEmployee(kindergartenId, employeeId);
      //toast.success('Працівника успішно поновлено!');
      // Перезагружаем данные
      await loadEmployees();
      await loadAvailablePositions();
    } catch (error) {
      console.error('Ошибка восстановления сотрудника:', error);
      //toast.error('Не вдалося поновити працівника');
    }
  };

  const ActionsMenu = ({ onActionSelect, onClose, employee }) => {
    const actions = [
      {
        id: 'edit',
        label: 'Перегляд',
        icon: faEdit
      }
    ];

    // Добавляем действие в зависимости от статуса
    if (employee.status === 'dismissed') {
      actions.push({
        id: 'restore',
        label: 'Поновити',
        icon: faUndo
      });
    } else {
      actions.push({
        id: 'dismiss',
        label: 'Звільнити',
        icon: faSignOutAlt
      });
    }

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, employee)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  // Функция для получения основной должности сотрудника
  const getEmployeePosition = (employee) => {
    const mainDept = employee.departments?.find(dept => dept.selected) || employee.departments?.[0];
    return mainDept?.positionName || 'Не вказано';
  };

  // Функция для получения общей суммы ставок
  const getEmployeeUnits = (employee) => {
    return employee.departments?.reduce((sum, dept) => sum + (parseFloat(dept.unit) || 0), 0) || 0;
  };

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Категорії працівників</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'pedagogical' ? 'active' : ''}`}
                      onClick={() => setActiveTab('pedagogical')}
                    >
                      Педагогічний
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'administrative' ? 'active' : ''}`}
                      onClick={() => setActiveTab('administrative')}
                    >
                      Адміністративний
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'technical' ? 'active' : ''}`}
                      onClick={() => setActiveTab('technical')}
                    >
                      Технічний
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'dismissed' ? 'active' : ''}`}
                      onClick={() => setActiveTab('dismissed')}
                    >
                      Звільнені
                    </button>
                  </li>
                </ul>
              </div>
              <div className="queuetable-switch-wrap">
                <a 
                  href="#" 
                  className="link-primary small text-nowrap" 
                  onClick={(e) => { e.preventDefault(); handleAddEmployee(); }}
                >
                  Оформити нового працівника
                </a>
              </div>
            </div>

            {/* Таблица */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Завантаження...</span>
                    </div>
                    <p className="mt-2">Завантаження даних...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table queuetable-table align-middle mb-0">
                        <thead>
                          <tr>
                            {columns.map((column) => (
                              <th 
                                key={column.key}
                                style={{ width: column.width }}
                                onClick={() => column.sortable && handleSort(column.key)}
                              >
                                <div className="d-flex align-items-center">
                                  {column.label}
                                  {column.sortable && (
                                    <FontAwesomeIcon 
                                      icon={getSortIcon(column.key)} 
                                      className="ms-1" 
                                      style={{ fontSize: '12px' }}
                                    />
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData.length > 0 ? (
                            sortedData.map((employee, index) => (
                              <tr key={employee.id}>
                                <td>{employee.fullName}</td>
                                <td>{getEmployeePosition(employee)}</td>
                                <td>
                                  {employee.appointment?.date ? 
                                    new Date(employee.appointment.date).toLocaleDateString('uk-UA') : 
                                    'Не вказано'
                                  }
                                </td>
                                <td>{getEmployeeUnits(employee)}</td>
                                <td className="queuetable-actions text-end">
                                  <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                    <button
                                      className="btn btn-sm btn-link text-decoration-none p-0"
                                      onClick={() => handleMenuToggle(index)}
                                      style={{ color: '#0d6efd' }}
                                    >
                                      <FontAwesomeIcon icon={faEllipsisVertical} />
                                    </button>
                                    
                                    {activeMenuIndex === index && (
                                      <ActionsMenu 
                                        onActionSelect={handleActionSelect}
                                        onClose={handleMenuClose}
                                        employee={employee}
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={columns.length} className="text-center py-4">
                                Немає даних для відображення
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Пагинация */}
                    <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                      <div className="queuetable-pagination-info">
                        Всього {sortedData.length} працівників
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно редактирования */}
      <EmployeeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        employeeData={selectedEmployee}
        onSave={handleSaveEmployee}
        onDismiss={handleDismissEmployee}
        onRestore={handleRestoreEmployee}
        availablePositions={availablePositions}
        kindergartenId={kindergartenId}
      />
    </>
  );
};

export default EmployeesList;